import React, { useState, useRef, useMemo, useCallback } from 'react'
import {
	Box,
	TextField,
	Paper,
	List,
	ListItem,
	ListItemAvatar,
	ListItemText,
	Avatar,
	IconButton,
	Typography,
	InputAdornment,
	ClickAwayListener,
	ListItemButton,
} from '@mui/material'
import {
	Search as SearchIcon,
	Close as CloseIcon,
	KeyboardArrowDown as ArrowDownIcon,
} from '@mui/icons-material'

const MemberListItem = React.memo(({ member, onSelect, isSelected }) => (
	<ListItem disablePadding>
		<ListItemButton
			onClick={() => onSelect(member)}
			sx={{
				py: 1.5,
				px: 2,
				'&:hover': {
					backgroundColor: '#F9FAFB',
				},
			}}
		>
			<ListItemAvatar sx={{ minWidth: 40 }}>
				<Avatar
					src={member.profilePic || member.avatar}
					alt={member.name}
					sx={{ width: 32, height: 32 }}
				/>
			</ListItemAvatar>
			<ListItemText
				primary={member.name}
				primaryTypographyProps={{
					fontSize: '14px',
					fontWeight: 400,
				}}
			/>
			{isSelected && (
				<Box sx={{ ml: 1 }}>
					<Box
						sx={{
							width: 16,
							height: 16,
							borderRadius: '50%',
							backgroundColor: '#10B981',
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'center',
						}}
					>
						<Box
							sx={{
								width: 6,
								height: 6,
								backgroundColor: 'white',
								borderRadius: '50%',
							}}
						/>
					</Box>
				</Box>
			)}
		</ListItemButton>
	</ListItem>
))

MemberListItem.displayName = 'MemberListItem'

const SelectedAvatar = React.memo(({ member, onRemove }) => (
	<Box sx={{ position: 'relative' }} title={member.name ?? ''}>
		<Avatar
			src={member.profilePic || member.avatar}
			alt={member.name}
			sx={{ width: 40, height: 40 }}
		/>
		<IconButton
			size="small"
			onClick={() => onRemove(member._id)}
			sx={{
				position: 'absolute',
				top: -6,
				right: -6,
				backgroundColor: '#000',
				color: 'white',
				width: 20,
				height: 20,
				'&:hover': {
					backgroundColor: '#374151',
				},
			}}
		>
			<CloseIcon sx={{ fontSize: 12 }} />
		</IconButton>
	</Box>
))

SelectedAvatar.displayName = 'SelectedAvatar'

const textFieldStyles = {
	'& .MuiOutlinedInput-root': {
		backgroundColor: 'white',
		fontSize: '14px',
		fontFamily: 'Inter',
		borderRadius: '8px',
	},
	'& .MuiInputBase-input': {
		padding: '10px 12px !important',
	},
	'& .MuiInputAdornment-root': {
		margin: '0 !important',
	},
	'& fieldset': {
		borderColor: '#DFE4EB',
	},
	'& .MuiOutlinedInput-notchedOutline': {
		borderColor: '#DFE4EB',
	},
	'&:hover .MuiOutlinedInput-notchedOutline': {
		borderColor: '#DFE4EB',
	},
	'&.Mui-focused .MuiOutlinedInput-notchedOutline': {
		borderColor: '#7ca6bd !important',
	},
}

export default function MultiselectSearch({
	members = [],
	selected = [],
	onSelect,
	onRemove,
	label,
	placeholder = 'Select',
	width = '100%',
	searchPlaceholder = 'Search...',
	slotProps = {},
}) {
	const [query, setQuery] = useState('')
	const [isOpen, setIsOpen] = useState(false)
	const inputRef = useRef(null)

	const selectedIds = useMemo(
		() => new Set(selected.map((m) => m._id)),
		[selected]
	)

	const filteredMembers = useMemo(() => {
		const lowerQuery = query.toLowerCase()
		return members.filter(
			(member) => {
				const target = member.searchName || member.name || ''
				return target.toLowerCase().includes(lowerQuery) &&
				!selectedIds.has(member._id)
			}
		)
	}, [members, query, selectedIds])

	const unselectedMembers = useMemo(
		() =>
			members
				.filter((member) => !selectedIds.has(member._id))
				.slice(0, 20),
		[members, selectedIds]
	)

	const handleSelect = useCallback(
		(member) => {
			onSelect?.(member)
			setQuery('')
			setIsOpen(false)
		},
		[onSelect]
	)

	const handleRemove = useCallback(
		(memberId) => {
			onRemove?.(memberId)
		},
		[onRemove]
	)

	const handleInputFocus = useCallback(() => {
		setIsOpen(true)
	}, [])

	const handleClickAway = useCallback(() => {
		setIsOpen(false)
		setQuery('')
	}, [])

	const handleInputChange = useCallback((e) => {
		setQuery(e.target.value)
		setIsOpen(true)
	}, [])

	const displayMembers = query ? filteredMembers : unselectedMembers
	const showNoResults = query && filteredMembers.length === 0
	const showAllSelected = !query && unselectedMembers.length === 0

	return (
		<ClickAwayListener onClickAway={handleClickAway}>
			<Box sx={{ width}}>
				<Box sx={{ position: 'relative' }}>
				{/* Label */}
				{label && (
					<Typography
						variant="body1"
						sx={{
							mb: 1,
							color: '#6B7280',
							fontSize: '14px',
							fontWeight: 400,
							textAlign: 'left',
						}}
					>
						{label}
					</Typography>
				)}

				{/* Main Input Field */}
				<TextField
					ref={inputRef}
					fullWidth
					value={query}
					onChange={handleInputChange}
					onFocus={handleInputFocus}
					placeholder={isOpen ? searchPlaceholder : placeholder}
					variant="outlined"
					InputProps={{
						startAdornment: isOpen ? (
							<InputAdornment position="start">
								<SearchIcon
									sx={{ color: '#9CA3AF', fontSize: 20 }}
								/>
							</InputAdornment>
						) : null,
						endAdornment: (
							<InputAdornment position="end">
								<ArrowDownIcon
									sx={{
										color: '#9CA3AF',
										fontSize: 20,
										transform: isOpen
											? 'rotate(180deg)'
											: 'rotate(0deg)',
										transition: 'transform 0.2s ease',
									}}
								/>
							</InputAdornment>
						),
					}}
					sx={textFieldStyles}
				/>

				{/* Dropdown List */}
				{isOpen && (
					<Paper
						elevation={3}
						sx={{
							position: 'absolute',
							top: '100%',
							left: 0,
							right: 0,
							mt: 1,
							zIndex: 1300,
							maxHeight: 200,
							overflow: 'auto',
							border: '1px solid #E5E7EB',
                            ...slotProps?.paper?.sx
						}}
                        {...slotProps?.paper}
					>
						<List sx={{ p: 0 }}>
							{showNoResults ? (
								<ListItem sx={{ py: 2, px: 2 }}>
									<ListItemText
										primary="No results found"
										primaryTypographyProps={{
											fontSize: '14px',
											color: '#9CA3AF',
										}}
									/>
								</ListItem>
							) : showAllSelected ? (
								<ListItem sx={{ py: 2, px: 2 }}>
									<ListItemText
										primary="No more results to select"
										primaryTypographyProps={{
											fontSize: '14px',
											color: '#9CA3AF',
										}}
									/>
								</ListItem>
							) : (
								displayMembers.map((member) => (
									<MemberListItem
										key={member._id}
										member={member}
										onSelect={handleSelect}
										isSelected={selectedIds.has(member._id)}
									/>
								))
							)}
						</List>
					</Paper>
				)}
			</Box>

				{/* Selected Items */}
				{selected.length > 0 && (
					<Box
						sx={{
							display: 'flex',
							flexWrap: 'wrap',
							gap: 1,
							mt: 2,
						}}
					>
						{selected.map((member) => (
							<SelectedAvatar
								key={member._id}
								member={member}
								onRemove={handleRemove}
							/>
						))}
					</Box>
				)}
			</Box>
		</ClickAwayListener>
	)
}
